import { useState } from 'react';
import { trpc } from '../lib/trpc';
import { useAuth } from '../hooks/useAuth';
import { HeartIcon, BookmarkIcon } from './ui/icons';
import { LoginPromptModal } from './ui/LoginPromptModal';
import { Tooltip } from './ui/Tooltip';

interface FavoriteButtonProps {
  entityType: 'venue' | 'wine' | 'tastingExperience';
  entityId: string;
  className?: string;
}

export const FavoriteButton = ({ entityType, entityId, className = '' }: FavoriteButtonProps) => {
  const { user } = useAuth();
  const utils = trpc.useUtils();
  const [optimisticFavorited, setOptimisticFavorited] = useState<boolean | null>(null);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  const isFavoriteVenue = trpc.user.isFavoriteVenue.useQuery(
    { venueId: entityId },
    { enabled: entityType === 'venue' && !!user }
  );

  const isFavoriteWine = trpc.user.isFavoriteWine.useQuery(
    { wineId: entityId },
    { enabled: entityType === 'wine' && !!user }
  );

  const toggleVenue = trpc.user.toggleFavoriteVenue.useMutation({
    onMutate: () => {
      setOptimisticFavorited((prev) => {
        const current = prev ?? isFavoriteVenue.data?.isFavorited ?? false;
        return !current;
      });
    },
    onSuccess: () => {
      setOptimisticFavorited(null);
      utils.user.isFavoriteVenue.invalidate({ venueId: entityId });
    },
    onError: () => {
      setOptimisticFavorited(null);
    },
  });

  const toggleWine = trpc.user.toggleFavoriteWine.useMutation({
    onMutate: () => {
      setOptimisticFavorited((prev) => {
        const current = prev ?? isFavoriteWine.data?.isFavorited ?? false;
        return !current;
      });
    },
    onSuccess: () => {
      setOptimisticFavorited(null);
      utils.user.isFavoriteWine.invalidate({ wineId: entityId });
    },
    onError: () => {
      setOptimisticFavorited(null);
    },
  });

  const isFavoriteTastingExperience = trpc.user.isFavoriteTastingExperience.useQuery(
    { tastingExperienceId: entityId },
    { enabled: entityType === 'tastingExperience' && !!user }
  );

  const toggleTastingExperience = trpc.user.toggleFavoriteTastingExperience.useMutation({
    onMutate: () => {
      setOptimisticFavorited((prev) => {
        const current = prev ?? isFavoriteTastingExperience.data?.isFavorited ?? false;
        return !current;
      });
    },
    onSuccess: () => {
      setOptimisticFavorited(null);
      utils.user.isFavoriteTastingExperience.invalidate({ tastingExperienceId: entityId });
    },
    onError: () => setOptimisticFavorited(null),
  });

  const serverFavorited =
    entityType === 'venue'
      ? isFavoriteVenue.data?.isFavorited ?? false
      : entityType === 'wine'
        ? isFavoriteWine.data?.isFavorited ?? false
        : isFavoriteTastingExperience.data?.isFavorited ?? false;

  const isFavorited = optimisticFavorited ?? serverFavorited;
  const isToggling =
    toggleVenue.isPending || toggleWine.isPending || toggleTastingExperience.isPending;

  const handleToggle = () => {
    if (!user) {
      setShowLoginPrompt(true);
      return;
    }
    if (isToggling) return;
    if (entityType === 'venue') toggleVenue.mutate({ venueId: entityId });
    else if (entityType === 'wine') toggleWine.mutate({ wineId: entityId });
    else toggleTastingExperience.mutate({ tastingExperienceId: entityId });
  };

  const tooltipLabel =
    entityType === 'wine'
      ? isFavorited
        ? 'Remove from my wishlist'
        : 'Add to my wishlist.'
      : isFavorited
        ? 'Remove from my favorites'
        : 'Add to my favorites.';

  return (
    <>
      <Tooltip label={tooltipLabel}>
        <button
          onClick={handleToggle}
          disabled={isToggling}
          className={`inline-flex items-center justify-center rounded-full p-2 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-info-500 focus:ring-offset-2 ${
            entityType === 'wine'
              ? isFavorited
                ? 'text-primary-700 hover:text-primary-800'
                : 'text-gray-700 hover:text-primary-700'
              : isFavorited
                ? 'text-red-500 hover:text-red-600'
                : 'text-gray-400 hover:text-red-400'
          } ${isToggling ? 'opacity-50' : ''} ${className}`}
          aria-label={tooltipLabel}
        >
          {entityType === 'wine' ? (
            <BookmarkIcon className="h-6 w-6" filled={isFavorited} />
          ) : (
            <HeartIcon className="h-6 w-6" filled={isFavorited} />
          )}
        </button>
      </Tooltip>
      <LoginPromptModal
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        feature={entityType === 'wine' ? 'Saving wines to your wishlist' : 'Saving favorites'}
      />
    </>
  );
};
